import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Play,
  ChevronRight,
  Timer,
  Sparkles,
  Heart,
  Coffee
} from 'lucide-react';
import { Button, Card, MoodSelector, type Mood } from '../../components/ui';
import { AppLayout } from '../../components/AppLayout';
import { CheckInReminderModal } from '../../components/CheckInReminderModal';
import { MusicIcon, VisualArtIcon, WritingIcon, VideoIcon, OtherIcon } from '../../components/icons';
import { useAuth } from '../../context';
import { userApi, UserProfile, dailyCheckInService, projectsApi, flowSessionsApi, ProjectCardDto, ProjectDto } from '../../services';
import './Dashboard.css';

// Encouraging messages based on time of day
const morningMessages = [
  "A fresh canvas awaits you today.",
  "The morning light is perfect for creating.",
  "What magic will you make today?",
  "Your creativity is stirring awake.",
];

const afternoonMessages = [
  "Inspiration strikes when you least expect it.",
  "Keep flowing, you're doing great.",
  "Every small step is still a step forward.",
  "Your art matters, even on slow days.",
];

const eveningMessages = [
  "The night holds endless possibilities.",
  "Some of the best ideas come after sunset.",
  "You showed up for your art today. That's everything.",
  "Rest is part of the creative process too.",
];

// Project encouragements
const projectEncouragements = [
  "Pick up where you left off?",
  "Your works miss you.",
  "Ready to create some magic?",
  "Which dream calls to you today?",
];

// Icon components by project type
const ProjectTypeIcon: React.FC<{ type: string; size?: number }> = ({ type, size = 24 }) => {
  switch (type) {
    case 'Music':
      return <MusicIcon size={size} />;
    case 'Art':
      return <VisualArtIcon size={size} />;
    case 'Writing':
      return <WritingIcon size={size} />;
    case 'Video':
      return <VideoIcon size={size} />;
    default:
      return <OtherIcon size={size} />;
  }
};

// Helper to format relative time
const formatRelativeTime = (dateString?: string): string => {
  if (!dateString) return 'Not yet';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`;
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} week${Math.floor(diffDays / 7) > 1 ? 's' : ''} ago`;
  return date.toLocaleDateString();
};

// Helper to format duration from ISO 8601 duration string
const formatDuration = (duration?: string): string => {
  if (!duration) return '0 min';
  // Parse ISO 8601 duration (e.g., "PT45M30S" or "PT1H30M")
  const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
  if (!match) return '0 min';
  const hours = parseInt(match[1] || '0');
  const minutes = parseInt(match[2] || '0');
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes} min`;
};

// Session display interface
interface SessionDisplay {
  id: string;
  projectTitle: string;
  duration: string;
  time: string;
  moodAfter?: string;
}

export const Dashboard: React.FC = () => {
  useAuth(); // Ensure user is authenticated
  const [hasCheckedIn, setHasCheckedIn] = useState(false);
  const [selectedMood, setSelectedMood] = useState<Mood | undefined>();
  const [dailyIntention, setDailyIntention] = useState<string | null>(null);
  const [encouragement, setEncouragement] = useState('');
  const [projectMessage, setProjectMessage] = useState('');
  const [isVisible, setIsVisible] = useState(false);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoadingCheckIn, setIsLoadingCheckIn] = useState(true);
  const [projects, setProjects] = useState<ProjectCardDto[]>([]);
  const [sessions, setSessions] = useState<SessionDisplay[]>([]);
  const [projectsMap, setProjectsMap] = useState<Map<string, ProjectDto>>(new Map());
  const [isLoadingProjects, setIsLoadingProjects] = useState(true);
  const [isLoadingSessions, setIsLoadingSessions] = useState(true);
  const [showCheckInModal, setShowCheckInModal] = useState(false);

  // Get user's display name
  const userName = profile?.name?.split(' ')[0] || 'Creator';

  // Fetch profile and check for existing check-in
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch profile
        const profileData = await userApi.getProfile();
        setProfile(profileData);
      } catch (err) {
        console.error('Error fetching profile:', err);
      }

      try {
        // Check for existing check-in today
        const todayCheckIn = await dailyCheckInService.getTodayCheckIn();
        if (todayCheckIn) {
          setHasCheckedIn(true);
          setSelectedMood(todayCheckIn.mood.toLowerCase() as Mood);
          setDailyIntention(todayCheckIn.dailyIntention);
        } else {
          // No check-in today - only show modal if this is a fresh login
          // Use sessionStorage to track if we've already shown the modal this session
          const modalShownThisSession = sessionStorage.getItem('checkinModalShown');
          if (!modalShownThisSession) {
            setShowCheckInModal(true);
            sessionStorage.setItem('checkinModalShown', 'true');
          }
        }
      } catch (err) {
        console.error('Error fetching today check-in:', err);
      } finally {
        setIsLoadingCheckIn(false);
      }
    };

    fetchData();
  }, []);

  // Fetch projects for dashboard
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const board = await projectsApi.getProjectBoard({ useUniversalStages: true });
        // Flatten all projects from columns and sort by last worked date
        const allProjects = board.columns
          .flatMap(col => col.projects)
          .sort((a, b) => {
            const aDate = a.lastWorkedDate ? new Date(a.lastWorkedDate).getTime() : 0;
            const bDate = b.lastWorkedDate ? new Date(b.lastWorkedDate).getTime() : 0;
            return bDate - aDate; // Most recent first
          })
          .slice(0, 3); // Show only top 3 recent projects
        setProjects(allProjects);

        // Also fetch full project list for session mapping
        const projectList = await projectsApi.getProjects();
        const map = new Map<string, ProjectDto>();
        projectList.forEach(p => map.set(p.id, p));
        setProjectsMap(map);
      } catch (err) {
        console.error('Error fetching projects:', err);
      } finally {
        setIsLoadingProjects(false);
      }
    };

    fetchProjects();
  }, []);

  // Fetch recent sessions
  useEffect(() => {
    const fetchSessions = async () => {
      // Wait for projects map to be loaded
      if (projectsMap.size === 0 && !isLoadingProjects) {
        setIsLoadingSessions(false);
        return;
      }
      if (projectsMap.size === 0) return; // Still loading projects

      try {
        const allSessions = await flowSessionsApi.getUserSessions();
        // Get completed sessions, sorted by most recent, limit to 5
        const recentSessions = allSessions
          .filter(s => !s.isActive && s.endTime)
          .sort((a, b) => new Date(b.endTime!).getTime() - new Date(a.endTime!).getTime())
          .slice(0, 5)
          .map(s => ({
            id: s.id,
            projectTitle: projectsMap.get(s.projectId)?.title || 'Unknown Work',
            duration: formatDuration(s.duration),
            time: formatRelativeTime(s.endTime),
            moodAfter: s.moodAfter,
          }));
        setSessions(recentSessions);
      } catch (err) {
        console.error('Error fetching sessions:', err);
      } finally {
        setIsLoadingSessions(false);
      }
    };

    fetchSessions();
  }, [projectsMap, isLoadingProjects]);

  useEffect(() => {
    const hour = new Date().getHours();
    let messages: string[];
    if (hour < 12) messages = morningMessages;
    else if (hour < 18) messages = afternoonMessages;
    else messages = eveningMessages;

    setEncouragement(messages[Math.floor(Math.random() * messages.length)]);
    setProjectMessage(projectEncouragements[Math.floor(Math.random() * projectEncouragements.length)]);
    setTimeout(() => setIsVisible(true), 100);
  }, []);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  const getGreetingEmoji = () => {
    const hour = new Date().getHours();
    if (hour < 12) return <Coffee className="greeting-icon" size={20} />;
    if (hour < 18) return <Sparkles className="greeting-icon" size={20} />;
    return <Heart className="greeting-icon" size={20} />;
  };

  const handleCheckIn = (mood: Mood) => {
    setSelectedMood(mood);
    setHasCheckedIn(true);
  };

  const handleModalCheckInComplete = (mood: Mood, intention?: string) => {
    setSelectedMood(mood);
    setDailyIntention(intention || null);
    setHasCheckedIn(true);
    setShowCheckInModal(false);
  };

  const getMoodMessage = (mood: Mood) => {
    const messages: Record<Mood, string> = {
      inspired: "That's wonderful! Let's channel that inspiration.",
      energized: "What amazing energy! Ready to create?",
      okay: "Every day can't be perfect. You're here, that's what matters.",
      tired: "It's okay to go slow. Even 15 minutes counts.",
      blocked: "Take a breath. We'll keep it gentle today.",
    };
    return messages[mood];
  };

  // Get CSS class for stage badge styling (WCAG compliant dark mode)
  const getStageClass = (stage: string): string => {
    const stageLower = stage?.toLowerCase() || '';
    const stageMap: Record<string, string> = {
      'idea': 'dashboard-project-card__stage--idea',
      'concept': 'dashboard-project-card__stage--concept',
      'outline': 'dashboard-project-card__stage--outline',
      'composition': 'dashboard-project-card__stage--composition',
      'draft': 'dashboard-project-card__stage--draft',
      'sketches': 'dashboard-project-card__stage--sketches',
      'recording': 'dashboard-project-card__stage--recording',
      'creation': 'dashboard-project-card__stage--creation',
      'mixing': 'dashboard-project-card__stage--mixing',
      'revision': 'dashboard-project-card__stage--revision',
      'mastering': 'dashboard-project-card__stage--mastering',
      'editing': 'dashboard-project-card__stage--editing',
      'refinement': 'dashboard-project-card__stage--refinement',
      'release': 'dashboard-project-card__stage--release',
      'publishing': 'dashboard-project-card__stage--publishing',
      'completion': 'dashboard-project-card__stage--complete',
      'complete': 'dashboard-project-card__stage--complete',
    };
    return stageMap[stageLower] || '';
  };

  return (
    <AppLayout hideBottomNav={false}>
    <div className="dashboard">
      {/* Check-in Reminder Modal */}
      <CheckInReminderModal
        isOpen={showCheckInModal}
        onClose={() => setShowCheckInModal(false)}
        onCheckInComplete={handleModalCheckInComplete}
        userName={userName}
      />

      {/* Ambient background */}
      <div className="dashboard-ambient">
        <div className="dashboard-ambient__shape dashboard-ambient__shape--1" />
        <div className="dashboard-ambient__shape dashboard-ambient__shape--2" />
      </div>

      {/* Greeting */}
      <div className={`dashboard-header ${isVisible ? 'dashboard-header--visible' : ''}`}>
        <div className="dashboard-header__content">
          <div className="dashboard-header__greeting">
            <div className="dashboard-greeting-row">
              {getGreetingEmoji()}
              <h1 className="dashboard-greeting">{getGreeting()}, {userName}</h1>
            </div>
            <p className="dashboard-encouragement">{encouragement}</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={`dashboard-main ${isVisible ? 'dashboard-main--visible' : ''}`}>
        {/* Check-in Card */}
        {isLoadingCheckIn ? (
          <Card variant="elevated" padding="lg" className="dashboard-checkin animate-slide-up">
            <div className="dashboard-checkin__loading">
              <div className="dashboard-checkin__spinner" />
              <p>Loading your check-in...</p>
            </div>
          </Card>
        ) : !hasCheckedIn ? (
          <Card variant="elevated" padding="lg" className="dashboard-checkin animate-slide-up">
            <div className="dashboard-checkin__header">
              <span className="dashboard-checkin__icon">🌱</span>
              <h2 className="dashboard-checkin__title">How are you feeling right now?</h2>
            </div>
            <p className="dashboard-checkin__subtitle">
              No judgment here. Just checking in.
            </p>
            <MoodSelector onChange={handleCheckIn} />
            <Link to="/checkin" className="dashboard-checkin__expand">
              Want to dive deeper? Full check-in →
            </Link>
          </Card>
        ) : (
          <Card variant="elevated" padding="md" className="dashboard-checkin-done animate-scale-in">
            <div className="dashboard-checkin-done__content">
              <span className="dashboard-checkin-done__emoji">
                {selectedMood === 'inspired' && '✨'}
                {selectedMood === 'energized' && '⚡'}
                {selectedMood === 'okay' && '🌤️'}
                {selectedMood === 'tired' && '🫖'}
                {selectedMood === 'blocked' && '🫂'}
              </span>
              <div className="dashboard-checkin-done__text">
                <span className="dashboard-checkin-done__mood">Feeling {selectedMood}</span>
                <span className="dashboard-checkin-done__message">{getMoodMessage(selectedMood!)}</span>
                {dailyIntention && (
                  <span className="dashboard-checkin-done__intention">
                    Focus: "{dailyIntention}"
                  </span>
                )}
              </div>
            </div>
            <Link to="/checkin" className="dashboard-checkin-done__edit">
              View
            </Link>
          </Card>
        )}

        {/* Gentle Prompt */}
        <div className="dashboard-prompt">
          <div className="dashboard-prompt__icon">💭</div>
          <p className="dashboard-prompt__text">{projectMessage}</p>
        </div>

        {/* Projects Section */}
        <section className="dashboard-section">
          <div className="dashboard-section__header">
            <h2 className="dashboard-section__title">Your Creative Works</h2>
            <Link to="/projects" className="dashboard-section__link">
              See all <ChevronRight size={16} />
            </Link>
          </div>
          <div className="dashboard-projects">
            {isLoadingProjects ? (
              <Card variant="outlined" padding="lg" className="dashboard-loading">
                <div className="dashboard-checkin__spinner" />
                <p>Loading your works...</p>
              </Card>
            ) : projects.length > 0 ? (
              <>
                {projects.map((project, index) => (
                  <Link
                    key={project.id}
                    to={project.category === 'Opus' ? `/projects/${project.id}/dashboard` : `/projects/${project.id}`}
                    style={{ textDecoration: 'none' }}
                  >
                    <Card
                      variant="default"
                      padding="md"
                      className="dashboard-project-card"
                      style={{ animationDelay: `${index * 0.1}s` }}
                    >
                      <div className="dashboard-project-card__bg-icon">
                        <ProjectTypeIcon type={project.projectType} size={64} />
                      </div>
                      <div className="dashboard-project-card__content">
                        <h3 className="dashboard-project-card__title">{project.title}</h3>
                        <span className={`dashboard-project-card__stage ${getStageClass(project.currentStage)}`}>
                          {project.currentStage}
                        </span>
                      </div>
                      <p className="dashboard-project-card__meta">
                        {formatRelativeTime(project.lastWorkedDate)} · {project.sessionCount} session{project.sessionCount !== 1 ? 's' : ''}
                      </p>
                      <div className="dashboard-project-card__cta">
                        <span>Continue</span>
                        <ChevronRight size={14} />
                      </div>
                    </Card>
                  </Link>
                ))}
              </>
            ) : null}
            {/* Add new project card */}
            <Link to="/projects" style={{ textDecoration: 'none' }}>
              <Card
                variant="outlined"
                padding="md"
                className="dashboard-project-card dashboard-project-card--new"
              >
                <div className="dashboard-project-card__plus">+</div>
                <p className="dashboard-project-card__new-text">Start something new</p>
              </Card>
            </Link>
          </div>
        </section>

        {/* Recent Sessions */}
        <section className="dashboard-section">
          <div className="dashboard-section__header">
            <h2 className="dashboard-section__title">Recent Moments</h2>
          </div>
          {isLoadingSessions ? (
            <Card variant="outlined" padding="lg" className="dashboard-loading">
              <div className="dashboard-checkin__spinner" />
              <p>Loading your sessions...</p>
            </Card>
          ) : sessions.length > 0 ? (
            <div className="dashboard-sessions">
              {sessions.map((session) => (
                <div key={session.id} className="dashboard-session-item">
                  <div className="dashboard-session-item__icon">
                    <Timer size={18} />
                  </div>
                  <div className="dashboard-session-item__content">
                    <span className="dashboard-session-item__project">{session.projectTitle}</span>
                    <span className="dashboard-session-item__meta">
                      {session.duration} · {session.time}{session.moodAfter ? ` · felt ${session.moodAfter.toLowerCase()}` : ''}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <Card variant="outlined" padding="lg" className="dashboard-empty">
              <span className="dashboard-empty__emoji">🎨</span>
              <p className="dashboard-empty__title">No sessions yet</p>
              <p className="dashboard-empty__subtitle">Even 10 minutes makes a difference.</p>
              <Button size="sm">Start Creating</Button>
            </Card>
          )}
        </section>

        {/* Gentle reminder */}
        <div className="dashboard-reminder">
          <span className="dashboard-reminder__icon">🌿</span>
          <p className="dashboard-reminder__text">
            Remember: Progress over perfection. You're doing great.
          </p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="dashboard-quick-actions">
        <Button className="dashboard-fab" size="lg">
          <Play size={24} /> Start Session
        </Button>
      </div>
    </div>
    </AppLayout>
  );
};

export default Dashboard;
